import { useEffect, useRef, useState } from "react";
import type { SugerenciaDireccion } from "@/lib/api";
import { MINIMO_BUSQUEDA } from "@/lib/terreno";
import { Combobox } from "./Combobox";

export interface BuscadorDireccionProps {
  /** Trae las sugerencias de lo escrito (ya con su token de sesión). */
  buscar: (texto: string) => Promise<SugerenciaDireccion[]>;
  /** Se llama al elegir una sugerencia (quien lo usa pide el detalle y completa el form). */
  onElegir: (sugerencia: SugerenciaDireccion) => void;
  /** Se llama si `buscar` falla (sin clave, Google caído): quien lo usa suele ocultarlo y dejar la carga a mano. */
  onFalla?: () => void;
  etiqueta?: string;
  placeholder?: string;
  /** Espera después de la última tecla antes de buscar (ms). */
  demora?: number;
  deshabilitado?: boolean;
}

/** Buscador de direcciones con sugerencias que llegan del servidor: espera `demora` ms desde la última tecla, busca
 * desde 3 letras y descarta respuestas viejas. Arriba de un `Combobox` en modo asíncrono. */
export function BuscadorDireccion({
  buscar, onElegir, onFalla, etiqueta = "Buscar dirección", placeholder = "Ej.: Av. Rivadavia 1234, Haedo", demora = 300, deshabilitado,
}: BuscadorDireccionProps) {
  const [texto, setTexto] = useState("");
  const [sugerencias, setSugerencias] = useState<SugerenciaDireccion[]>([]);
  const [cargando, setCargando] = useState(false);
  const ultima = useRef(0);
  const buscarRef = useRef(buscar);
  buscarRef.current = buscar;
  const fallaRef = useRef(onFalla);
  fallaRef.current = onFalla;

  useEffect(() => {
    const pedido = ++ultima.current;
    const q = texto.trim();
    if (q.length < MINIMO_BUSQUEDA) {
      setSugerencias([]);
      setCargando(false);
      return;
    }
    setCargando(true);
    const timer = setTimeout(() => {
      buscarRef
        .current(q)
        .then((lista) => {
          if (pedido === ultima.current) setSugerencias(lista);
        })
        .catch(() => {
          if (pedido === ultima.current) {
            setSugerencias([]);
            fallaRef.current?.();
          }
        })
        .finally(() => {
          if (pedido === ultima.current) setCargando(false);
        });
    }, demora);
    return () => clearTimeout(timer);
  }, [texto, demora]);

  const corto = texto.trim().length < MINIMO_BUSQUEDA;
  return (
    <Combobox
      etiqueta={etiqueta}
      placeholder={placeholder}
      deshabilitado={deshabilitado}
      opciones={sugerencias.map((s) => ({ valor: s.place_id, etiqueta: s.texto, detalle: s.secundario }))}
      onBuscar={setTexto}
      cargando={cargando}
      textoVacio={corto ? (texto.trim() ? `Escribí al menos ${MINIMO_BUSQUEDA} letras` : null) : "No encontramos esa dirección"}
      onElegir={(placeId) => {
        const elegida = sugerencias.find((s) => s.place_id === placeId);
        if (elegida) onElegir(elegida);
      }}
    />
  );
}
